import type {CurrencyCode, ObjectFieldsAssertion, Values} from '@wix/devzai-utils-common';
import {arrayRemoveNullValues, currencyFormatPrice, currencyGetAllCodes, urlJoin} from '@wix/devzai-utils-common';
import type {WixProduct} from '@wix/devzai-common-wix';
import {wixProductMediaTryConvertToWixMediaResource} from '@wix/devzai-common-wix';
import type {DoppeActionPriceData} from './doppe-action-price-data';
import type {DoppeActionPrice} from './doppe-action-price';

export const DoppeDtoStoreProductType = {
    Unspecified: 'unspecified_product_type',
    Physical: 'physical',
    Digital: 'digital'
} as const;

type DoppeDtoStoreProductType = Values<typeof DoppeDtoStoreProductType>;

export interface DoppeDtoStoreProduct {
    id: string;
    slug?: string;
    name: string;
    description: string;
    descriptionPlainText: string;
    media?: WixProduct.Media;
    price: DoppeDtoStoreProduct.PriceData;
    productPageUrl?: string;
    discount?: WixProduct.Discount;
    stock?: WixProduct.Stock;
    productType: DoppeDtoStoreProductType;
    ribbon?: WixProduct['ribbon'];
    productOptions?: WixProduct.ProductOption[];
    additionalInfoSections?: WixProduct.AdditionalInfoSection[];
    customTextFields?: WixProduct.CustomTextField[];
}

export namespace DoppeDtoStoreProduct {
    export type Id = DoppeDtoStoreProduct['id'];

    export type PriceData = WixProduct.PriceData;

    export type ProductType = DoppeDtoStoreProductType;

    export type ForUpdate = Pick<DoppeDtoStoreProduct, 'id' | 'price' | 'name' | 'descriptionPlainText' | 'description' | 'media' | 'productType'>

    export type ForCreation = Pick<DoppeDtoStoreProduct, 'price' | 'name'| 'descriptionPlainText' | 'description' | 'media' | 'productType'>
}

export function doppeStoreProductResolveUrl(productPageUrl: WixProduct.PageUrl) {
    if (productPageUrl?.base && productPageUrl?.path) {
        return urlJoin(productPageUrl.base,productPageUrl.path);
    }

    return undefined;
}

export const DoppeDtoStoreProductForCreationFieldsAssertions: ObjectFieldsAssertion<DoppeDtoStoreProduct.ForCreation> = {
    name: assert => assert.isString(),
    description: assert => assert.isString(),
    descriptionPlainText: assert => assert.isString(),
    price: assert => assert.usingAssertionFunction(() => {
        // TODO
    }),
    media: assert => assert.optional.usingAssertionFunction(() => {
        // TODO
    }),
    productType: assert => assert.usingAssertionFunction(() => {
        // TODO
    }),
}

export const DoppeDtoStoreProductForUpdateFieldsAssertions: ObjectFieldsAssertion<DoppeDtoStoreProduct.ForUpdate> = {
    id: assert => assert.isString(),
    name: assert => assert.isString(),
    description: assert => assert.isString(),
    descriptionPlainText: assert => assert.isString(),
    price: assert => assert.usingAssertionFunction(() => {
        // TODO
    }),
    media: assert => assert.optional.usingAssertionFunction(() => {
        // TODO
    }),
    productType: assert => assert.usingAssertionFunction(() => {
        // TODO
    }),
}

export function doppeDtoStoreProductPriceDataConvertToActionPriceData (price: DoppeDtoStoreProduct.PriceData) : DoppeActionPriceData {
    const actualPrice = doppeDtoStoreProductConvertToFormattedPrice(price, '');
    const discountPrice = doppeDtoStoreProductConvertToFormattedDiscountPrice(price, '');

    return {
        actualPrice: actualPrice,
        discountPrice: actualPrice !== discountPrice ? discountPrice : ''
    }
}

export function doppeDtoStoreProductPriceDataConvertToActionPrice(price: DoppeDtoStoreProduct.PriceData) : DoppeActionPrice {
    return {
        actualPrice: price.price ?? 1,
        discountPrice: price.discountedPrice,
        currencyCode: price.currency ? price.currency as CurrencyCode : 'USD'
    }
}

export function doppeDtoProductGetMedia (dtoStoreProduct: DoppeDtoStoreProduct) {
    return arrayRemoveNullValues([
        dtoStoreProduct.media?.mainMedia ? wixProductMediaTryConvertToWixMediaResource(dtoStoreProduct.media?.mainMedia) : null,
    ])
}

export function doppeDtoProductGetMediaItems (dtoStoreProduct: DoppeDtoStoreProduct) {
    return arrayRemoveNullValues(dtoStoreProduct.media?.items?.map(item => wixProductMediaTryConvertToWixMediaResource(item)) ?? [])
}

export function doppeDtoStoreProductConvertToFormattedPrice<EmptyValue>(priceData: DoppeDtoStoreProduct.PriceData, emptyValue: EmptyValue): string | EmptyValue {
    const currency = priceData.currency as CurrencyCode;

    return priceData.price && (priceData.currency && currencyGetAllCodes().includes(currency)) ? currencyFormatPrice({
        code: currency,
        price: priceData.price
    }) : (priceData.formatted?.price ?? emptyValue)
}

export function doppeDtoStoreProductConvertToFormattedDiscountPrice<EmptyValue>(priceData: DoppeDtoStoreProduct.PriceData, emptyValue: EmptyValue): string | EmptyValue {
    const currency = priceData.currency as CurrencyCode;

    return priceData.discountedPrice && (priceData.currency && currencyGetAllCodes().includes(currency)) ? currencyFormatPrice({
        code: currency,
        price: priceData.discountedPrice
    }) : (priceData.formatted?.discountedPrice ?? emptyValue)
}